import { Box, Card, Flex, Grid, GridItem, Heading, Text } from '@chakra-ui/react';
import { formatDate } from '@utils/date';
import { useAuthContext } from '@contexts/auth';

import type { CampaignExecutionType } from '../types';
import FinishButton from './FinishButton';
import ManageUsersButton from './ManageUsersButton';
import ExecutionsButtons from './ExecutionsButtons';

type Props = {
  execution: CampaignExecutionType,
  refetch: () => void,
};

const InfoLine = ({
  label, value,
}: { label: string, value?: string | number | null }) => (
  <Flex gap={2} mb={2} align="center">
    <Text fontSize="sm" color="text.muted" fontWeight={600}>{label}:</Text>
    <Text fontSize="md">{value}</Text>
  </Flex>
);

const CampaignExecutionCard = ({ execution, refetch }: Props) => {
  const { checkIfHasRole } = useAuthContext();

  return (
    <Card.Root
      size="sm"
      variant="elevated"
      bg="grayscale.fg"
      mt={4}
      overflow="hidden"
    >
      <Box px={4} py={3}>
        <Flex justifyContent="space-between" gap={2} mb={3}>
          <Box>
            <Heading size="md">
              {execution.campaign.name}
            </Heading>
            <Text color="fg.muted">
              {execution.portfolio.name}
            </Text>
          </Box>
          <Flex>
            {checkIfHasRole('CAN_FINISH_CAMPAIGN_EXECUTION') && (
              <FinishButton
                executionId={execution.id}
                afterSubmit={refetch}
              />
            )}
            {checkIfHasRole('CAN_MANAGE_CAMPAIGN_EXECUTION') && (
              <ManageUsersButton
                executionId={execution.id}
              />
            )}
            {checkIfHasRole('CAN_PERFORM_CAMPAIGN_EXECUTION') && (
              <ExecutionsButtons executionId={execution.id} />
            )}
          </Flex>
        </Flex>
        <Grid templateColumns="repeat(6, 1fr)" gap="2">
          <GridItem colSpan={{ base: 6, md: 3, lg: 2 }}>
            <InfoLine
              label="Total"
              value={execution.total}
            />
          </GridItem>
          <GridItem colSpan={{ base: 6, md: 3, lg: 2 }}>
            <InfoLine
              label="Restantes"
              value={execution.remaining}
            />
          </GridItem>
          <GridItem colSpan={{ base: 6, md: 3, lg: 2 }}>
            <InfoLine
              label="Finalizados"
              value={execution.completed}
            />
          </GridItem>
          <GridItem colSpan={{ base: 6, md: 3, lg: 2 }}>
            <InfoLine
              label="Aguardando"
              value={execution.waiting}
            />
          </GridItem>
          <GridItem colSpan={{ base: 6, md: 3, lg: 2 }}>
            <InfoLine
              label="Início"
              value={formatDate(execution.start_date, "dd/MM/yyyy 'às' HH:mm'h'")}
            />
          </GridItem>
          <GridItem colSpan={{ base: 6, md: 3, lg: 2 }}>
            <InfoLine
              label="Término"
              value={formatDate(execution.end_date, "dd/MM/yyyy 'às' HH:mm'h'") || '-'}
            />
          </GridItem>
          <GridItem colSpan={6}>
            <InfoLine
              label="Atendendo"
              value={execution.current_customer ? execution.current_customer.name : '-'}
            />
          </GridItem>
        </Grid>
      </Box>

      {execution.waiting > 0 && (
        <Box bg="red.500" px={2} py={1}>
          <Text textAlign="center" color="white">
            Você possui
            {' '}
            <b>
              {execution.waiting}
            </b>
            {' '}
            atendimento(s) pendente(s).
            O mais antigo está em aberto desde
            {' '}
            <b>
              {formatDate(execution.last_interaction)}
            </b>
            .
            Finalize-o(s) conforme necessário.
          </Text>
        </Box>
      )}
    </Card.Root>
  );
};

export default CampaignExecutionCard;
